import type { FC, ReactNode } from 'react'
import type { FestiveColor } from '@/types'

import { cn } from '@/lib/utils'

type ConfettiShape = 'dot' | 'ring' | 'pill' | 'triangle' | 'squiggle' | 'star'

export type ConfettiPiece = {
    shape: ConfettiShape
    color: FestiveColor
    top: string
    left: string
    size: number
    rotate?: number
    delay?: number
    duration?: number
    hideOnMobile?: boolean
}

interface ConfettiProps {
    pieces: Array<ConfettiPiece>
    className?: string
}

const COLOR_CLASSES: Record<FestiveColor, string> = {
    saffron: 'text-festive-saffron',
    raspberry: 'text-festive-raspberry',
    teal: 'text-festive-teal',
    lilac: 'text-festive-lilac',
    coral: 'text-festive-coral',
    blush: 'text-festive-blush'
}

const ConfettiShapeSvg: FC<{ shape: ConfettiShape }> = ({ shape }): ReactNode => {
    switch (shape) {
        case 'dot':
            return (
                <svg viewBox='0 0 10 10' className='block h-auto w-full'>
                    <circle cx='5' cy='5' r='5' fill='currentColor' />
                </svg>
            )
        case 'ring':
            return (
                <svg viewBox='0 0 20 20' className='block h-auto w-full'>
                    <circle cx='10' cy='10' r='7' fill='none' stroke='currentColor' strokeWidth='3.5' />
                </svg>
            )
        case 'pill':
            return (
                <svg viewBox='0 0 24 8' className='block h-auto w-full'>
                    <rect width='24' height='8' rx='4' fill='currentColor' />
                </svg>
            )
        case 'triangle':
            return (
                <svg viewBox='0 0 20 18' className='block h-auto w-full'>
                    <path d='M10 2 18 16H2Z' fill='currentColor' stroke='currentColor' strokeWidth='2.5' strokeLinejoin='round' />
                </svg>
            )
        case 'squiggle':
            return (
                <svg viewBox='0 0 40 12' className='block h-auto w-full'>
                    <path d='M2 6c3-5 6-5 9 0s6 5 9 0 6-5 9 0 6 5 9 0' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
                </svg>
            )
        case 'star':
            return (
                <svg viewBox='0 0 24 24' className='block h-auto w-full'>
                    <path d='M12 0c1.2 6.6 5.4 10.8 12 12-6.6 1.2-10.8 5.4-12 12-1.2-6.6-5.4-10.8-12-12C6.6 10.8 10.8 6.6 12 0Z' fill='currentColor' />
                </svg>
            )
    }
}

const Confetti: FC<ConfettiProps> = ({ pieces, className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
            {pieces.map((piece, index) => (
                <span
                    key={`${piece.shape}-${index}`}
                    className={cn(
                        'absolute block animate-[confetti-float_7s_ease-in-out_infinite] motion-reduce:animate-none',
                        COLOR_CLASSES[piece.color],
                        piece.hideOnMobile && 'hidden sm:block'
                    )}
                    style={{
                        top: piece.top,
                        left: piece.left,
                        width: `${piece.size}rem`,
                        rotate: `${piece.rotate ?? 0}deg`,
                        animationDelay: `${piece.delay ?? 0}s`,
                        animationDuration: `${piece.duration ?? 7}s`
                    }}
                >
                    <ConfettiShapeSvg shape={piece.shape} />
                </span>
            ))}
        </div>
    )
}

export const HERO_CONFETTI: Array<ConfettiPiece> = [
    { shape: 'star', color: 'saffron', top: '13%', left: '5%', size: 1.6, delay: 0 },
    { shape: 'dot', color: 'raspberry', top: '34%', left: '2%', size: 0.7, delay: 1.2, hideOnMobile: true },
    { shape: 'squiggle', color: 'teal', top: '84%', left: '4%', size: 2.6, rotate: -12, delay: 0.6 },
    { shape: 'ring', color: 'lilac', top: '64%', left: '47%', size: 1.3, delay: 2, hideOnMobile: true },
    { shape: 'triangle', color: 'coral', top: '17%', left: '52%', size: 1.1, rotate: 18, delay: 0.3, hideOnMobile: true },
    { shape: 'pill', color: 'saffron', top: '92%', left: '40%', size: 1.8, rotate: 35, delay: 1.6 },
    { shape: 'star', color: 'raspberry', top: '11%', left: '90%', size: 1.2, delay: 0.9 },
    { shape: 'dot', color: 'teal', top: '52%', left: '95%', size: 0.9, delay: 2.4 },
    { shape: 'squiggle', color: 'raspberry', top: '93%', left: '80%', size: 2.4, rotate: 8, delay: 1.1, hideOnMobile: true },
    { shape: 'pill', color: 'lilac', top: '42%', left: '55%', size: 1.5, rotate: -30, delay: 0.4, hideOnMobile: true }
]

export const BAND_CONFETTI: Array<ConfettiPiece> = [
    { shape: 'star', color: 'saffron', top: '18%', left: '3%', size: 1.3, delay: 0.2 },
    { shape: 'dot', color: 'raspberry', top: '75%', left: '8%', size: 0.8, delay: 1.4 },
    { shape: 'squiggle', color: 'teal', top: '12%', left: '46%', size: 2.2, rotate: 10, delay: 0.8, hideOnMobile: true },
    { shape: 'ring', color: 'coral', top: '80%', left: '52%', size: 1.1, delay: 2.1, hideOnMobile: true },
    { shape: 'triangle', color: 'lilac', top: '22%', left: '93%', size: 1, rotate: -20, delay: 1 },
    { shape: 'pill', color: 'saffron', top: '86%', left: '90%', size: 1.6, rotate: 40, delay: 0.5 }
]

export const SOFT_CONFETTI: Array<ConfettiPiece> = [
    { shape: 'star', color: 'saffron', top: '8%', left: '4%', size: 1.4, delay: 0.3 },
    { shape: 'squiggle', color: 'teal', top: '46%', left: '2%', size: 2.2, rotate: -8, delay: 1.2, hideOnMobile: true },
    { shape: 'dot', color: 'lilac', top: '90%', left: '12%', size: 0.9, delay: 0.7 },
    { shape: 'triangle', color: 'coral', top: '6%', left: '62%', size: 1, rotate: 24, delay: 1.8, hideOnMobile: true },
    { shape: 'ring', color: 'raspberry', top: '94%', left: '70%', size: 1.2, delay: 0.1, hideOnMobile: true },
    { shape: 'star', color: 'raspberry', top: '14%', left: '94%', size: 1.1, delay: 2.2 }
]

export default Confetti
